import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import * as offerActions from '../../../actions/actionCreators/offer/offerActions';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import OfferList from './OfferList';
import OfferListTopToolbar from './OfferListTopToolbar';
import { Grid } from '@mui/material';
import { clean, getDateValue, getInt, objToQueryString } from 'utils/functions';
import GridOn from '@mui/icons-material/GridOn';
import HeaderPortal from '../../layout/HeaderPortal';
import documentTypeIds from 'constants/documentTypeIds';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import { Card } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { OffersListWrapper } from './OfferListStyles';
import WrappedFakeText from 'components/common/WrappedFakeText';
import useDebounce from 'utils/hooks/useDebounce';
import { emptyString } from 'constants/emptyConst';
import * as visibilityLevels from 'constants/visibilityLevels';

const DEBOUNCE_DELAY = 500;

/*  Sub atatea caractere nu se mai trimite cautarea la server - vezi stripShortFullTextFilter. */
const MIN_FULL_TEXT_FILTER_LENGTH = 3;

const blankFilters = {
  dealerId: null,
  userPartnerId: null,
  accountManagerId: null,
  currencyId: null,
  financingTypeId: null,
  offerTypeId: null,
  channelTypeId: null,
  approvalTypeLevelId: null,
  offerPriorityId: null,
  transactionCategoryId: null,
  sourceTypeId: null,
  fullTextFilter: '',
  offerNumber: null,
  offerStartDate: null,
  offerEndDate: null,
  clientName: null,
  isActive: true,
  stateIds: [],
  userId: null,
  responsibleId: null,
  orderByColumnName: 'InternalOfferDate',
  orderByDescending: true,
  visibilityLevelId: visibilityLevels.MyDocuments
};

const mandatoryFields = ['clientName', 'offerNumber'];
const defaultPager = { page: 1, pageSize: 10, totalCount: 0 };

const OfferListContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const userPreferences = useSelector(state => state.offerVisible.userPreferences, shallowEqual);
  const selectedUserPreference = useSelector(state => state.offerVisible.selectedUserPreference, shallowEqual);
  const defaultUserPreference = useSelector(state => state.offerVisible.defaultUserPreference, shallowEqual);

  const isSearchingRestrictedResponsible = userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedResponsible;
  const isSearchingRestrictedDealer = userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedDealer;
  const userIsJustSalesUser = userInfo.isLoaded && userInfo.userIsJustSalesUser;

  const [offers, setOffers] = useState(null);
  const [filters, setFilters] = useState(null);
  const [pager, setPager] = useState(defaultPager);
  const [currentSelectedUserPreference, setCurrentSelectedUserPreference] = useState(null);

  const visibleFilters = currentSelectedUserPreference?.filters || selectedUserPreference.filters;
  const visibleFields = currentSelectedUserPreference?.fields || selectedUserPreference.fields;
  const fieldsToShow = visibleFields.filter(x => x.isVisible).map(x => x.fieldName);

  const debounceFiltersValueChanged = useDebounce(filters, DEBOUNCE_DELAY);

  const initCurrentPreference = newPreference => initFilters({ ...getFilterValuesFromUserPreference(newPreference), fullTextFilter: '' });

  const getFiltersFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      dealerId: getInt(url.get('dealerId')),
      userId: getInt(url.get('userId')),
      userPartnerId: getInt(url.get('userPartnerId')),
      accountManagerId: getInt(url.get('accountManagerId')),
      currencyId: getInt(url.get('currencyId')),
      financingTypeId: getInt(url.get('financingTypeId')),
      offerTypeId: getInt(url.get('offerTypeId')),
      channelTypeId: getInt(url.get('channelTypeId')),
      approvalTypeLevelId: getInt(url.get('approvalTypeLevelId')),
      offerPriorityId: getInt(url.get('offerPriorityId')),
      transactionCategoryId: getInt(url.get('transactionCategoryId')),
      sourceTypeId: getInt(url.get('sourceTypeId')),
      fullTextFilter: url.get('fullTextFilter'),
      offerNumber: url.get('offerNumber'),
      offerStartDate: url.get('offerStartDate'),
      offerEndDate: url.get('offerEndDate'),
      clientName: url.get('clientName'),
      isActive: JSON.parse(url.get('isActive')),
      stateIds: url.getAll('stateIds').flatMap(v => v.split(',')).map(getInt).filter(id => id !== null),
      responsibleId: getInt(url.get('responsibleId')),
      visibilityLevelId: getInt(url.get('visibilityLevelId')),
      orderByDescending: JSON.parse(url.get('orderByDescending')),
      orderByColumnName: url.get('orderByColumnName')
    };
  };

  const getPagerFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      page: getInt(url.get('page')) ?? defaultPager.page,
      pageSize: getInt(url.get('pageSize')) ?? defaultPager.pageSize,
      totalCount: defaultPager.totalCount
    };
  };

  const getFilterValuesFromUserPreference = useCallback((userPreference) => {
    let newFilters = { ...blankFilters };
    if (!userPreference) return newFilters;

    userPreference.filters.forEach(el => { newFilters[el.fieldName] = el.defaultValue; });
    if (userPreference.order) {
      newFilters.orderByColumnName = userPreference.order.orderByColumnName;
      newFilters.orderByDescending = userPreference.order.orderByDescending;
    }

    return getFiltersFromURL(objToQueryString(newFilters));
  }, []);

  const getQueryStringFromFiltersAndPaging = (filters, pager) =>
    objToQueryString({
      ...filters,
      page: pager.page === defaultPager.page ? '' : pager.page,
      pageSize: pager.pageSize === defaultPager.pageSize ? '' : pager.pageSize
    });

  const stripShortFullTextFilter = searchFilters => {
    const trimmed = searchFilters?.fullTextFilter?.trim() ?? '';
    return trimmed.length > 0 && trimmed.length < MIN_FULL_TEXT_FILTER_LENGTH
      ? { ...searchFilters, fullTextFilter: '' }
      : searchFilters;
  };

  const filterOffers = useCallback((searchFilters, searchPager) => {
    async function loadOffersAsync() {
      const loadedOffers = await dispatch(
        offerActions.loadOffers(getQueryStringFromFiltersAndPaging(stripShortFullTextFilter(searchFilters), searchPager))
      );
      setOffers(loadedOffers);
    }

    loadOffersAsync();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const pushFiltersAndPagingToQueryString = useCallback((newFilters, newPager) => {
    if (!userPreferences.isLoaded) return;
    if (newFilters?.offerStartDate) newFilters.offerStartDate = getDateValue(newFilters.offerStartDate);
    if (newFilters?.offerEndDate) newFilters.offerEndDate = getDateValue(newFilters.offerEndDate);
    if (newFilters?.offerStartDate !== emptyString && newFilters?.offerEndDate !== emptyString) {
      history.push('/offers?' + getQueryStringFromFiltersAndPaging(newFilters, newPager));
      filterOffers(newFilters, newPager);
    }
  }, [userPreferences.isLoaded, history, filterOffers]);

  const getDefaults = useCallback(() => {
    return {
      ...blankFilters,
      userPartnerId: isSearchingRestrictedDealer || selectedUserPreference.isDefault ? userInfo.userExtraInfoModel.userPartnerId : null,
      responsibleId: isSearchingRestrictedResponsible ? userInfo.userExtraInfoModel.responsibleId : null,
      accountManagerId: userInfo.userExtraInfoModel.accountManagerId,
    };
  }, [isSearchingRestrictedDealer, isSearchingRestrictedResponsible, selectedUserPreference.isDefault, userInfo.userExtraInfoModel.accountManagerId, userInfo.userExtraInfoModel.responsibleId, userInfo.userExtraInfoModel.userPartnerId]);

  const initFilters = useCallback(async (receivedFilters) => {
    let newFilters = { ...receivedFilters };

    if (newFilters.responsibleId === userInfo.userExtraInfoModel.responsibleId)
      newFilters.responsible = { responsibleId: userInfo.userExtraInfoModel.responsibleId, name: userInfo.userExtraInfoModel.responsibleName };
    else if (newFilters.responsibleId !== null && (!newFilters?.responsible || newFilters.responsibleId !== newFilters?.responsible?.responsibleId))
      newFilters.responsible = await dispatch(dictionariesActions.loadResponsible(newFilters.responsibleId));

    if (newFilters.accountManagerId === userInfo.userExtraInfoModel.accountManagerId)
      newFilters.accountManager = { accountManagerId: userInfo.userExtraInfoModel.accountManagerId, name: userInfo.userExtraInfoModel.accountManagerName };
    else if (newFilters.accountManagerId !== null && (!newFilters?.accountManager || newFilters.accountManagerId !== newFilters?.accountManager?.accountManagerId))
      newFilters.accountManager = await dispatch(dictionariesActions.loadAccountManager(newFilters.accountManagerId));

    if (newFilters.userPartnerId === userInfo.userExtraInfoModel.userPartnerId)
      newFilters.userPartner = { partnerId: userInfo.userExtraInfoModel.userPartnerId, name: userInfo.userExtraInfoModel.userPartnerName };
    else if (newFilters.userPartnerId !== null && (!newFilters?.userPartner || newFilters.userPartnerId !== newFilters?.userPartner?.userPartnerId))
      newFilters.userPartner = await dispatch(dictionariesActions.loadUserPartner(newFilters.userPartnerId));

    if (newFilters.dealerId !== null && (!newFilters?.dealer || newFilters.dealerId !== newFilters?.dealer?.dealerId))
      newFilters.dealer = await dispatch(dictionariesActions.loadDealer(newFilters.dealerId));

    if (newFilters.visibilityLevelId !== null && (!newFilters?.visibilityLevel || newFilters.visibilityLevelId !== newFilters?.visibilityLevel?.visibilityLevelId))
      newFilters.visibilityLevel = await dispatch(dictionariesActions.loadOfferVisibilityLevels()).then(data => { return data?.find(d => d.visibilityLevelId === newFilters.visibilityLevelId); });

    if (newFilters.channelTypeId !== null && (!newFilters?.channelType || newFilters.channelTypeId !== newFilters?.channelType?.channelTypeId))
      newFilters.channelType = await dispatch(dictionariesActions.loadChannels()).then(data => { return data?.find(d => d.channelTypeId === newFilters.channelTypeId); });

    if (newFilters.stateIds?.length > 0)
      newFilters.states = await dispatch(dictionariesActions.loadStates(documentTypeIds.Offer)).then(data => { return data?.filter(d => newFilters.stateIds.includes(d.stateId)); });
    else
      newFilters.states = [];

    if (newFilters.userId !== null && (!newFilters?.user || newFilters.userId !== newFilters?.user?.userId))
      newFilters.user = await dispatch(dictionariesActions.loadUser(newFilters.userId));

    if (newFilters.currencyId !== null && (!newFilters?.currency || newFilters.currencyId !== newFilters?.currency?.currencyId))
      newFilters.currency = await dispatch(dictionariesActions.loadCurrencies()).then(data => { return data?.find(d => d.currencyId === newFilters.currencyId); });

    if (newFilters.financingTypeId !== null && (!newFilters?.financingType || newFilters.financingTypeId !== newFilters?.financingType?.financingTypeId))
      newFilters.financingType = await dispatch(dictionariesActions.loadFinancingTypes()).then(data => { return data?.find(d => d.financingTypeId === newFilters.financingTypeId); });

    if (newFilters.approvalTypeLevelId !== null && (!newFilters?.approvalTypeLevel || newFilters.approvalTypeLevelId !== newFilters?.approvalTypeLevel?.approvalTypeLevelId))
      newFilters.approvalTypeLevel = await dispatch(dictionariesActions.loadApprovalTypes()).then(data => { return data?.find(d => d.approvalTypeLevelId === newFilters.approvalTypeLevelId); });

    if (newFilters.offerPriorityId !== null && (!newFilters?.offerPriority || newFilters.offerPriorityId !== newFilters?.offerPriority?.offerPriorityId))
      newFilters.offerPriority = await dispatch(dictionariesActions.loadPartnerPriorities()).then(data => { return data?.find(d => d.offerPriorityId === newFilters.offerPriorityId); });

    if (newFilters.transactionCategoryId !== null && (!newFilters?.transactionCategory || newFilters.transactionCategoryId !== newFilters?.transactionCategory?.transactionCategoryId))
      newFilters.transactionCategory = await dispatch(dictionariesActions.loadTransactionCategories()).then(data => { return data?.find(d => d.transactionCategoryId === newFilters.transactionCategoryId); });

    if (newFilters.sourceTypeId !== null && (!newFilters?.sourceType || newFilters.sourceTypeId !== newFilters?.sourceType?.sourceTypeId))
      newFilters.sourceType = await dispatch(dictionariesActions.loadSources()).then(data => { return data?.find(d => d.sourceTypeId === newFilters.sourceTypeId); });

    if (newFilters.offerTypeId !== null && (!newFilters?.offerType || newFilters.offerTypeId !== newFilters?.offerType?.offerTypeId))
      newFilters.offerType = await dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.Offer))
        .then(offerTypes => {
          const modificationOfferTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.OfferModification));
          return Promise.all([modificationOfferTypes, offerTypes]);
        })
        .then(([modificationOfferTypes, offerTypes]) => {
          const facilityOfferTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.FacilityFrameOffer));
          return Promise.all([facilityOfferTypes, modificationOfferTypes, offerTypes]);
        })
        .then(([facilityOfferTypes, modificationOfferTypes, offerTypes]) => {
          return offerTypes.concat(facilityOfferTypes).concat(modificationOfferTypes).find(type => type.offerTypeId === newFilters.offerTypeId);
        });

    setFilters(newFilters);
  }, [dispatch, userInfo.userExtraInfoModel]);

  const initUserPreference = useCallback(() => {
    const matchedUserPreferences = userPreferences.filter(function (up) {
      const userPrefenceStringFilters = getFilterValuesFromUserPreference(up);
      const queryStringFilters = getFiltersFromURL(objToQueryString(debounceFiltersValueChanged));
      clean(userPrefenceStringFilters);
      clean(queryStringFilters);

      if (Object.keys(userPrefenceStringFilters).length !== Object.keys(queryStringFilters).length)
        return false;

      let matchedFilters = 0;
      Object.keys(userPrefenceStringFilters).forEach(function (key) {
        const preferenceValue = userPrefenceStringFilters[key];
        const queryValue = queryStringFilters[key];
        const isEqual = Array.isArray(preferenceValue) || Array.isArray(queryValue)
          ? String(preferenceValue) === String(queryValue)
          : preferenceValue === queryValue;
        if (isEqual) matchedFilters++;
      });

      return matchedFilters === Object.keys(queryStringFilters).length;
    });

    if (!!matchedUserPreferences && matchedUserPreferences.length > 0)
      setCurrentSelectedUserPreference(userPreferences.find(up => up.filterName === matchedUserPreferences[0].filterName));
    else
      setCurrentSelectedUserPreference(prevState => { return { ...(prevState ?? selectedUserPreference), filterName: '' } });
  }, [debounceFiltersValueChanged, selectedUserPreference, userPreferences, getFilterValuesFromUserPreference]);

  useEffect(() => {
    dispatch(offerActions.loadUserPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (!userPreferences.isLoaded || filters != null) return;
    let newFilters;
    if (location.search !== '') {
      setPager(getPagerFromURL(location.search));
      newFilters = getFiltersFromURL(location.search);
    }
    else {
      const filtersUserPreference = getFilterValuesFromUserPreference(userPreferences.find(up => up.implicit) ?? defaultUserPreference);
      clean(filtersUserPreference);
      newFilters = { ...getDefaults(), ...filtersUserPreference };
    }
    initFilters({ ...newFilters, fullTextFilter: newFilters.fullTextFilter ?? '' });
  }, [userPreferences, defaultUserPreference, filters, location.search, getDefaults, getFilterValuesFromUserPreference, initFilters, initUserPreference]);

  useEffect(() => {
    if (!debounceFiltersValueChanged) return;
    initUserPreference();
    pushFiltersAndPagingToQueryString(debounceFiltersValueChanged, pager);
  }, [debounceFiltersValueChanged, pager, pushFiltersAndPagingToQueryString, initUserPreference]);

  const onRefresh = () => filterOffers(filters, pager);
  const onAdd = () => history.push('/offers/new');
  const onAddRestructuring = () => history.push('/offers/restructuring/new');
  const onEdit = offer => {
    sessionStorage.setItem("OfferURLFilters", location.search);
    return `/offers/${offer.documentId}/${offer.siteId}`;
  }

  const onDownload = async fileType => {
    const queryStringFilters = getFiltersFromURL(location.search);

    clean(queryStringFilters);
    const defaultFilters = getFilterValuesFromUserPreference(currentSelectedUserPreference);
    const filters = {
      ...blankFilters,
      ...defaultFilters,
      ...queryStringFilters,
      fileType
    };
    const fieldsToShow = currentSelectedUserPreference?.fields
      .filter(x => x.isVisible === true)
      .map(x => {
        let field = x.fieldName;
        if (field.toLowerCase() === 'responsibleid') field = 'responsibleName';
        if (field.toLowerCase() === 'sourcetypeid') field = 'sourceTypeName';
        return { fieldName: field, label: t('OfferList.' + x.label) };
      });

    const event = await dispatch(offerActions.exportOffers(filters, fieldsToShow));
    window.location = dispatch(offerActions.buildOfferExportDownloadUrl(event.filePath, event.fileName));
  };

  const onResetFilters = () => {
    initFilters({
      ...blankFilters,
      userPartnerId: isSearchingRestrictedDealer ? userInfo.userExtraInfoModel.userPartnerId : null,
      responsibleId: isSearchingRestrictedResponsible ? userInfo.userExtraInfoModel.responsibleId : null
    });
  }

  const onFilterValueChanged = (name, value) => {
    setFilters(prevState => {
      return {
        ...prevState,
        [name]: value,
        visibilityLevelId: name === 'visibilityLevel' ? value?.visibilityLevelId : prevState.visibilityLevelId,
        responsibleId: name === 'responsible' ? value?.responsibleId : prevState.responsibleId,
        channelTypeId: name === 'channelType' ? value?.channelTypeId : prevState.channelTypeId,
        partnerTypeId: name === 'partnerType' ? value?.partnerTypeId : prevState.partnerTypeId,
        stateIds: name === 'states' ? (value || []).map(v => v.stateId) : prevState.stateIds,
        riskAnalysisTypeId: name === 'riskAnalysisType' ? value?.riskAnalysisTypeId : prevState.riskAnalysisTypeId,
        dealerId: name === 'dealer' ? value?.dealerId : prevState.dealerId,
        userPartnerId: name === 'userPartner' ? value?.partnerId : prevState.userPartnerId,
        userId: name === 'user' ? value?.userId : name === 'userPartner' ? null : prevState.userId,
        user: name === 'userPartner' ? null : name === 'user' ? value : prevState.user,
        currencyId: name === 'currency' ? value?.currencyId : prevState.currencyId,
        relationTypeId: name === 'relationType' ? value?.relationTypeId : prevState.relationTypeId,
        offerTypeId: name === 'offerType' ? value?.offerTypeId : prevState.offerTypeId,
        accountManagerId: name === 'accountManager' ? value?.accountManagerId : prevState.accountManagerId,
        financingTypeId: name === 'financingType' ? value?.financingTypeId : prevState.financingTypeId,
        approvalTypeLevelId: name === 'approvalTypeLevel' ? value?.approvalTypeLevelId : prevState.approvalTypeLevelId,
        offerPriorityId: name === 'offerPriority' ? value?.offerPriorityId : prevState.offerPriorityId,
        transactionCategoryId: name === 'transactionCategory' ? value?.transactionCategoryId : prevState.transactionCategoryId,
        sourceTypeId: name === 'sourceType' ? value?.sourceTypeId : prevState.sourceTypeId
      }
    });
  };

  const onFilterVisibleValueChanged = (name, value) => {
    const filter = visibleFilters.find(x => x.fieldName === name);
    const index = visibleFilters.indexOf(filter);
    const newVisibleFilters = Object.assign([...visibleFilters], { [index]: { ...filter, isVisible: value } });
    setCurrentSelectedUserPreference({ ...selectedUserPreference, filterName: '', implicit: false, isDefault: false, filters: newVisibleFilters });
  };

  const onFieldsVisibleValueChanged = (name, value) => {
    if (mandatoryFields.includes(name)) return;

    const filter = visibleFields.find(x => x.fieldName === name);
    const index = visibleFields.indexOf(filter);
    const newVisibleFields = Object.assign([...visibleFields], { [index]: { ...filter, isVisible: value } });
    if (!newVisibleFields.some(x => x.isVisible === true)) return;

    setCurrentSelectedUserPreference({ ...selectedUserPreference, filterName: '', implicit: false, isDefault: false, fields: newVisibleFields });
  };

  const onChangePage = pageChanged => setPager(prev => { return { ...prev, page: pageChanged + 1 } });
  const onChangeRowsPerPage = pageSizeChanged => setPager(prev => { return { ...prev, pageSize: pageSizeChanged, page: 1 } });
  const onUserPreferenceChanged = name => initCurrentPreference(userPreferences.find(x => x.filterName === name));

  const onAddUserPreference = popUpUserPreference => {
    if (popUpUserPreference.filterName === '' || userPreferences.some(x => x.filterName === popUpUserPreference.filterName)) return;

    const newPreference = {
      ...popUpUserPreference,
      isDefault: false,
      implicit: false,
      filterName: popUpUserPreference.filterName.trim(),
      filters: popUpUserPreference.filters.map(f => { return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? '' } })
    };
    dispatch(offerActions.updateUserPreferences([...userPreferences, newPreference]));
  };

  const onDeleteUserPreference = () => {
    if (currentSelectedUserPreference.isDefault) {
      dispatch(showInfo(t('OfferList.CantDeleteDefaultFilter')));
      return;
    }

    dispatch(offerActions.updateUserPreferences(userPreferences.filter(p => p.filterName !== currentSelectedUserPreference.filterName)));
    const newPreference = {
      ...currentSelectedUserPreference,
      isDefault: false,
      implicit: false,
      filterName: '',
      filters: currentSelectedUserPreference.filters.map(f => { return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? f.defaultValue } })
    };
    dispatch(offerActions.updateSelectedUserPreference(newPreference));
    initCurrentPreference(newPreference);
  };

  const onListDeleteChanged = item => {
    if (item.isDefault) {
      dispatch(showInfo(t('OfferList.CantDeleteDefaultFilter')));
      return;
    }

    if (item.filterName === currentSelectedUserPreference.filterName)
      setCurrentSelectedUserPreference(prevState => { return { ...prevState, isDefault: false, filterName: '', implicit: false } });

    dispatch(offerActions.updateUserPreferences(userPreferences.filter(p => p.filterName !== item.filterName)));
  };

  const onListImplicitChanged = item => {
    const newUserPreferences = userPreferences.map(up => { return { ...up, implicit: up.filterName === item.filterName ? !item.implicit : false }; });
    dispatch(offerActions.updateUserPreferences(newUserPreferences));
    const newPreference = newUserPreferences.find(p => p.implicit === true);
    if (newPreference) {
      dispatch(offerActions.updateSelectedUserPreference(newPreference));
      initCurrentPreference(newPreference);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <Card icon={GridOn}>
          <OffersListWrapper>
            {debounceFiltersValueChanged &&
              <OfferListTopToolbar
                filters={filters || { ...blankFilters }}
                onFilterValueChanged={onFilterValueChanged}
                onFilterVisibleValueChanged={onFilterVisibleValueChanged}
                onFieldsVisibleValueChanged={onFieldsVisibleValueChanged}
                onResetFilters={onResetFilters}
                onAdd={onAdd}
                onAddRestructuring={onAddRestructuring}
                onDownload={onDownload}
                onUserPreferenceChanged={onUserPreferenceChanged}
                onAddUserPreference={onAddUserPreference}
                onDeleteUserPreference={onDeleteUserPreference}
                onListDeleteChanged={onListDeleteChanged}
                onListImplicitChanged={onListImplicitChanged}
                downloadEnabled={offers?.totalCount > 0}
                isSearchingRestrictedUserPartner={isSearchingRestrictedDealer}
                isSearchingRestrictedResponsible={isSearchingRestrictedResponsible}
                visibleFilters={[...visibleFilters]}
                visibleFields={[...visibleFields]}
                userIsJustSalesUser={userIsJustSalesUser}
                userPreferences={userPreferences}
                selectedUserPreference={currentSelectedUserPreference || selectedUserPreference}
              />
            }

            {!offers ? <WrappedFakeText /> : <OfferList offers={offers.values} fieldsToShow={fieldsToShow} onEditLink={onEdit} />}

            <PaginationWrapper
              count={offers?.totalCount ?? defaultPager.totalCount}
              pageSize={offers?.pageSize ?? defaultPager.pageSize}
              page={offers?.page ?? defaultPager.page}
              onRowsPerPageChange={onChangeRowsPerPage}
              onPageChange={onChangePage}
              onRefresh={onRefresh}
            />
          </OffersListWrapper>
        </Card>
      </Grid>
      <HeaderPortal />
    </Grid>
  );
};

export default OfferListContainer;
